import { useState } from 'react'
import { useNavigate } from 'react-router'
import type { JSX, SyntheticEvent } from 'react'
import { api, call } from '@/api/client'
import { useMutation } from '@/api/query'
import { useAuth } from '@/auth/context'
import { DEFAULT_HOME } from '@/auth/routes'
import { useTenants } from '@/tenant/context'
import { Lockup } from '@/shell/Lockup'
import { initials, slugify } from '@/lib/format'
import { ThemeToggle } from '@/components/ThemeToggle'
import { Button } from '@/ui/Button'
import { TextField } from '@/ui/Field'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

/** The slug rule, mirrored from `CreateTenantRequest` so the message is immediate. */
const SLUG = /^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/

/**
 * Pick an organisation, or make one.
 *
 * Outside the shell, because the shell's sidebar names the organisation you are
 * in and there is not one yet. Three audiences land here and all three are
 * served: a brand new account with none, someone who belongs to several, and
 * anyone who was removed from the one their session pointed at.
 */
export function Organisations(): JSX.Element {
  const { memberships, status, failure, active, select, reload } = useTenants()
  const { signOut, user } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()
  const [creating, setCreating] = useState(false)

  const switching = useMutation((tenantId: string) => selectAndReport(tenantId))

  async function selectAndReport(tenantId: string) {
    const ok = await select(tenantId)
    return ok
      ? ({ ok: true, data: tenantId } as const)
      : ({
          ok: false,
          failure: {
            code: 'NOT_A_MEMBER',
            message: 'You are no longer a member of that organisation.',
            requestId: '',
            status: 403,
            retryAfterSeconds: null,
          },
        } as const)
  }

  return (
    <div className="app-public">
      <div className="ar-row" style={{ width: 'min(40rem, 100%)' }}>
        <span className="app-public__lockup">
          <Lockup />
        </span>
        <span className="app-spacer" />
        <ThemeToggle />
        <Button
          tone="quiet"
          size="sm"
          onClick={() => {
            void signOut()
          }}
        >
          Sign out
        </Button>
      </div>

      <section className="ar-card" style={{ width: 'min(40rem, 100%)' }}>
        <div className="ar-card__header">
          <h3>Your organisations</h3>
          <span className="ar-card__spacer" />
          {memberships.length > 0 && !creating ? (
            <Button
              tone="primary"
              size="sm"
              onClick={() => {
                setCreating(true)
              }}
            >
              New organisation
            </Button>
          ) : null}
        </div>

        {status === 'loading' ? (
          <div className="ar-card__body">
            <Loading label="Loading your organisations" lines={2} />
          </div>
        ) : null}

        {status === 'error' && failure ? (
          <ErrorState
            failure={failure}
            title="Could not load your organisations"
            onRetry={reload}
          />
        ) : null}

        {status === 'ready' && memberships.length === 0 && !creating ? (
          <Empty
            icon="◫"
            title="No organisation yet"
            body={
              user?.email
                ? `Create one to start, or ask a teammate to invite ${user.email}. An invitation is addressed to your email, so it has to be sent to that exact address.`
                : 'Create one to start, or ask a teammate to send you an invitation.'
            }
            action={
              <Button
                tone="primary"
                onClick={() => {
                  setCreating(true)
                }}
              >
                Create an organisation
              </Button>
            }
          />
        ) : null}

        {status === 'ready' && memberships.length > 0 ? (
          <div className="ar-card__body ar-stack ar-stack--tight">
            <InlineError failure={switching.state.failure} />
            {memberships.map((row) => (
              <button
                key={row.tenant.id}
                type="button"
                className="app-tenant"
                disabled={switching.pending}
                onClick={() => {
                  void (async () => {
                    const result = await switching.run(row.tenant.id)
                    if (result.ok) void navigate(DEFAULT_HOME)
                  })()
                }}
              >
                <span className="ar-avatar" aria-hidden="true">
                  {initials(row.tenant.name)}
                </span>
                <span className="app-tenant__body">
                  <strong>{row.tenant.name}</strong>
                  <span>
                    {row.membership.role} · {row.tenant.timezone}
                  </span>
                </span>
                <span className="ar-card__spacer" />
                {row.tenant.id === active?.tenant.id ? (
                  <span className="ar-badge ar-badge--success">Current</span>
                ) : null}
              </button>
            ))}
          </div>
        ) : null}

        {creating ? (
          <CreateOrganisation
            onCancel={() => {
              setCreating(false)
            }}
            onCreated={(name) => {
              toast.show({ kind: 'success', title: `${name} is ready`, body: 'You are its owner.' })
              // Into the wizard, not onto an empty dashboard: a brand-new
              // organisation has no site, no keywords and no voice, and every
              // screen the shell offers would be empty until it does.
              void navigate('/onboarding')
            }}
          />
        ) : null}
      </section>
    </div>
  )
}

/**
 * The create form.
 *
 * The slug is shown, pre-filled from the name and editable, rather than derived
 * silently: it is **immutable** once set (`update_tenant` accepts name and
 * timezone only), so a value the user never saw is one they cannot change
 * afterwards.
 */
function CreateOrganisation({
  onCancel,
  onCreated,
}: {
  onCancel: () => void
  onCreated: (name: string) => void
}): JSX.Element {
  const { adopt } = useTenants()
  const { refresh } = useAuth()
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [touchedSlug, setTouchedSlug] = useState(false)

  const create = useMutation((body: { name: string; slug: string }) =>
    call(() => api.POST('/api/v1/tenants', { body })),
  )

  const effectiveSlug = touchedSlug ? slug : slugify(name)
  const slugError =
    effectiveSlug.length > 0 && !SLUG.test(effectiveSlug)
      ? 'Lowercase letters, numbers and hyphens; it cannot start or end with a hyphen.'
      : undefined

  async function onSubmit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    if (slugError) return
    const result = await create.run({ name: name.trim(), slug: effectiveSlug })
    if (!result.ok) return
    // Both halves, in the order the shell needs them. `adopt` puts the new
    // organisation in the list *now* rather than starting a refetch, and
    // `refresh` re-reads the session, which the server has already pointed at
    // it. Without the first, navigating to the shell finds a session naming an
    // organisation the list does not have yet and bounces straight back here —
    // which is exactly what the first live run did.
    adopt({ tenant: result.data.tenant, membership: result.data.membership })
    await refresh()
    onCreated(result.data.tenant.name)
  }

  return (
    <form
      onSubmit={(event) => {
        void onSubmit(event)
      }}
    >
      <div className="ar-card__body ar-stack">
        <InlineError failure={create.state.failure} />
        <TextField
          label="Organisation name"
          required
          autoFocus
          maxLength={120}
          help="What your team calls the business. You can change this later."
          value={name}
          onChange={(event) => {
            setName(event.target.value)
            create.reset()
          }}
        />
        <TextField
          label="Short name"
          required
          value={effectiveSlug}
          error={slugError}
          help="Used in links and reports. This one cannot be changed afterwards."
          onChange={(event) => {
            setTouchedSlug(true)
            setSlug(event.target.value)
            create.reset()
          }}
        />
      </div>
      <div className="ar-card__footer">
        <span className="ar-card__spacer" />
        <Button tone="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          tone="primary"
          type="submit"
          busy={create.pending}
          busyLabel="Creating"
          disabled={name.trim().length === 0 || effectiveSlug.length === 0 || Boolean(slugError)}
        >
          Create organisation
        </Button>
      </div>
    </form>
  )
}
